import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import api from '../../../services/api';
import { toast } from 'sonner';
import { AutoMergePreviewDialog } from './AutoMergePreviewDialog';

/**
 * Auto-merge banner for the Duplicate Management page.
 *
 * Auto-merge can match thousands of high-confidence pairs (each phone+email
 * exact match). Running them all in a single HTTP request hits the 60 s
 * gateway timeout. Instead we batch the apply call (`limit=25`) and loop
 * client-side until the backend reports `merged === 0`, surfacing live
 * progress in the toast/banner so the admin sees what's happening.
 *
 * Preview now opens a full-list dialog (with search + CSV export) so the
 * admin can sanity-check every pair before clicking apply — this matters
 * because apply *deletes* the source record.
 */
export const AutoMergeBanner = ({ onApplied }) => {
  const [running, setRunning] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [progress, setProgress] = useState(null); // { merged, total }
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post('/admin/duplicates/parents/auto-merge');
      setPreviewData(data);
      setPreviewOpen(true);
      if ((data.total_qualifying || 0) === 0) {
        toast.info(data.message || 'No qualifying pairs.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async (selectedRows) => {
    const rows = selectedRows || [];
    if (rows.length === 0) return;
    setRunning(true);
    setProgress({ merged: 0, total: rows.length });
    let totalMerged = 0;
    let totalErrors = 0;
    const t = toast.loading(`Merging ${rows.length} selected pair(s)…`);
    try {
      // Slice client-side into chunks of 25 and send only the selected pairs.
      const CHUNK = 25;
      for (let start = 0; start < rows.length; start += CHUNK) {
        const chunk = rows.slice(start, start + CHUNK).map((r) => ({
          source_id: r.source_id,
          target_id: r.target_id,
        }));
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post(
          '/admin/duplicates/parents/auto-merge-selected',
          { pairs: chunk },
        );
        totalMerged += data.merged ?? 0;
        totalErrors += (data.errors || []).length;
        setProgress({ merged: totalMerged, total: rows.length });
        toast.message(
          `Merging… ${totalMerged} of ${rows.length}${totalErrors ? ` (${totalErrors} error${totalErrors === 1 ? '' : 's'})` : ''}`,
          { id: t },
        );
      }
      toast.success(
        `Merged ${totalMerged} of ${rows.length} pair(s)${totalErrors ? `; ${totalErrors} error(s)` : ''}.`,
        { id: t },
      );
      setPreviewOpen(false);
      setPreviewData(null);
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(
        e.response?.data?.detail
          || `Merge stopped after ${totalMerged} pair(s)`,
        { id: t },
      );
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <>
      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardContent className="p-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex-1 min-w-[260px]">
            <p className="text-sm font-semibold text-emerald-800">
              Auto-Merge High-Confidence Duplicates
            </p>
            <p className="text-xs text-emerald-700/80">
              Click Preview to review every pair the matcher proposes before applying. Older
              account is kept; the other is deleted after transferring children, enrollments &amp; bookings.
            </p>
            {progress && (
              <div className="mt-2 space-y-1" data-testid="auto-merge-progress">
                <Progress
                  value={progress.total ? Math.min(100, (progress.merged / progress.total) * 100) : null}
                  className="h-1.5"
                />
                <p className="text-xs text-emerald-800">
                  {progress.merged} merged{progress.total ? ` of ~${progress.total}` : ''}…
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={running || previewLoading}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              data-testid="auto-merge-preview-btn"
              onClick={handlePreview}
            >
              {previewLoading ? 'Loading…' : 'Preview Auto-Merge'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AutoMergePreviewDialog
        open={previewOpen}
        onOpenChange={(o) => { if (!running) setPreviewOpen(o); }}
        preview={previewData}
        onConfirmApply={handleApply}
        applying={running}
      />
    </>
  );
};
